import { motion } from "framer-motion";
import { Code2, Server, Braces, Database, BarChart3, Cloud, ShieldCheck, Workflow, Cable } from "lucide-react";
import { EASE } from "../../lib/motion";

const TIERS = [
  { label: "Experiencia", items: [{ icon: Code2, name: "React" }] },
  { label: "Servicios", items: [{ icon: Server, name: "Node.js" }, { icon: Braces, name: "APIs" }] },
  { label: "Datos", items: [{ icon: Database, name: "PostgreSQL" }, { icon: BarChart3, name: "Analytics" }] },
  { label: "Nube", items: [{ icon: Cloud, name: "Cloud" }, { icon: ShieldCheck, name: "Security" }, { icon: Workflow, name: "Automation" }] },
];

export default function TechArchitecture() {
  return (
    <div className="dark-section relative overflow-hidden rounded-[2rem] border border-white/10 px-6 py-16 sm:px-12 lg:px-16" data-testid="tech-architecture">
      <div className="absolute inset-0 grid-bg opacity-40 [mask-image:radial-gradient(ellipse_70%_70%_at_50%_50%,black,transparent)]" aria-hidden="true" />
      <div className="pointer-events-none absolute -top-32 left-1/2 h-72 w-[640px] -translate-x-1/2 rounded-full bg-brand/25 blur-[120px]" aria-hidden="true" />

      <div className="relative mx-auto max-w-3xl text-center">
        <motion.span
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE }}
          className="eyebrow justify-center text-slate-400 before:bg-sky-400"
        >
          DASOFT Engineering
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
          className="mt-4 text-3xl font-bold text-white sm:text-4xl"
        >
          Technology behind the business
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
          className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base"
        >
          Diseñamos, construimos y operamos la tecnología que sostiene el negocio: desde la
          interfaz que usa tu equipo hasta los datos que respaldan cada decisión.
        </motion.p>
      </div>

      <div className="relative mx-auto mt-14 max-w-4xl">
        {TIERS.map((tier, i) => (
          <div key={tier.label}>
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.16, ease: EASE }}
              className="flex flex-col items-center gap-4 sm:flex-row"
            >
              <span className="hidden w-28 shrink-0 text-right font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 sm:block">
                {tier.label}
              </span>
              <div className="flex flex-1 flex-wrap justify-center gap-3 sm:justify-start">
                {tier.items.map((it) => (
                  <span
                    key={it.name}
                    className="group flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm font-medium text-slate-200 backdrop-blur transition-all duration-300 hover:border-sky-400/50 hover:bg-sky-400/10"
                    data-testid={`tech-node-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <it.icon className="h-4 w-4 text-sky-400" strokeWidth={1.8} />
                    {it.name}
                  </span>
                ))}
              </div>
            </motion.div>
            {i < TIERS.length - 1 && (
              <div className="relative ml-auto mr-auto flex h-9 w-min justify-center overflow-hidden" aria-hidden="true">
                <span className="h-full w-px bg-gradient-to-b from-transparent via-sky-400/40 to-transparent" />
                <motion.span
                  className="absolute h-1.5 w-1.5 rounded-full bg-sky-400"
                  initial={{ top: "-10%", opacity: 0 }}
                  whileInView={{ top: ["-10%", "110%"], opacity: [0, 1, 0] }}
                  viewport={{ once: false, amount: 0.1 }}
                  transition={{ delay: 0.4 + i * 0.2, duration: 1, repeat: Infinity, repeatDelay: 1.6, ease: "easeInOut" }}
                />
              </div>
            )}
          </div>
        ))}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-10 flex items-center justify-center gap-2 text-center font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500"
        >
          <Cable className="h-3.5 w-3.5" />
          Arquitectura conectada de extremo a extremo
        </motion.div>
      </div>
    </div>
  );
}
